import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import services from 'services';
import { Space, State } from 'models';

import { getCurrentCooperative } from 'store/home/selectors';

import { BalanceLoanDetails } from 'components';
import PageLayout from 'layouts/PageLayout';

function extractSpace(spaces: Space[] | undefined) {
  return spaces && spaces.length ? spaces[0] : null;
}

interface BalanceLoanState {
  spaces: Space[];
  selectedSpace: Space | null;
  loading: boolean;
  error: boolean;
  isValidReport: boolean;
  validatingReport: boolean;
}

const BalanceLoan = () => {
  const { currentCooperative, coopName } = useSelector((state: State) => ({
    currentCooperative: state.settings.current_cooperative,
    coopName: getCurrentCooperative(state)?.Name || null,
  }));

  const [state, setState] = useState<BalanceLoanState>({
    spaces: [],
    selectedSpace: null,
    loading: false,
    error: false,
    isValidReport: false,
    validatingReport: false,
  });

  const fetchSpaces = async (coopId: string) => {
    try {
      setState((prevState) => ({
        ...prevState,
        loading: true,
        error: false,
      }));

      const spaces: Space[] = (
        await services.get(`/api/user/owner/cooperatives/${coopId}/spaces`)
      ).data.Data;

      setState((prevState) => ({
        ...prevState,
        spaces,
        selectedSpace: extractSpace(spaces),
        loading: false,
        error: false,
      }));
    } catch (err) {
      console.error(err);
      setState((prevState) => ({
        ...prevState,
        loading: false,
        error: true,
      }));
    }
  };

  const validateReport = async (spaceId: string) => {
    let isValidReport = false;

    try {
      setState((prevState) => ({
        ...prevState,
        validatingReport: true,
      }));

      const res = (
        await services.get<{ Data: { CanCreateReport: boolean } }>(
          `/reports/space/balanceloan/${spaceId}/check`
        )
      ).data;

      isValidReport = res.Data.CanCreateReport ?? false;
    } catch (err) {
      console.error(err);
    } finally {
      setState((prevState) => ({
        ...prevState,
        validatingReport: false,
        isValidReport,
      }));
    }
  };

  const handleChangeSpace = useCallback((space: Space) => {
    setState((prevState) => ({
      ...prevState,
      selectedSpace: space,
    }));
  }, []);

  const fileName = useMemo(() => {
    return `balanceloan__${coopName}`;
  }, [coopName]);

  const noData = !state.spaces.length || !state.selectedSpace;

  useEffect(() => {
    if (currentCooperative) {
      fetchSpaces(currentCooperative);
    }
  }, [currentCooperative]);

  useEffect(() => {
    if (state.selectedSpace) {
      validateReport(state.selectedSpace.Id);
    }
  }, [state.selectedSpace]);

  return (
    <PageLayout
      isLoading={state.loading || state.validatingReport}
      isError={state.error}
      noData={noData}
    >
      <BalanceLoanDetails
        isValidReport={state.isValidReport}
        selectedSpace={state.selectedSpace}
        spaces={state.spaces}
        fileName={fileName}
        handleChangeSpace={handleChangeSpace}
        validatingInProgress={state.validatingReport}
      />
    </PageLayout>
  );
};

export default BalanceLoan;
